<template>
  <div class="user-goal">
    <c-page-section squares class="q-mb-lg">
      <q-card
        :class="{
          'goal-done': goal.done,
          'goal-active': goal.active,
          'goal-after-deadline': !goal.done && !goal.active,
        }"
      >
        <q-card-section class="goal-card-section q-px-md">
          <div class="row items-center no-wrap">
            <div class="col">
              <div class="heading-bigger" data-testid="goal-heading">
                {{ formatGoalNumber(goalIndex) }}
                <div v-if="allowRead" class="visible-user-name" data-testid="visible-user-name">
                  ({{ goal.collaborator.name }})
                </div>
              </div>
              <div v-if="goal.startDate">
                {{
                  t('userGoals.startDateLabel', {
                    date: shortDotFormat(parseISO(goal.startDate)),
                  })
                }}
              </div>
              <div v-if="goal.deadline">
                {{
                  t('userGoals.deadline', {
                    deadline: shortDotFormat(parseISO(goal.deadline)),
                  })
                }}
              </div>
            </div>
            <div v-if="allowEdit" class="col-auto row no-wrap items-center">
              <q-icon
                data-testid="edit-goal"
                name="edit"
                color="grey"
                size="sn"
                class="cursor-pointer q-pr-xs"
                @click="clickEdit(goal.id)"
              >
                <q-tooltip>
                  {{ t('userGoals.tooltip.edit') }}
                </q-tooltip>
              </q-icon>
              <q-icon
                data-testid="delete-goal"
                color="grey"
                name="delete_outline"
                size="sm"
                class="cursor-pointer"
                @click="clickDelete(goal.id)"
              >
                <q-tooltip>
                  {{ t('userGoals.tooltip.delete') }}
                </q-tooltip>
              </q-icon>
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="overflow-auto">
          <wysiwyg-editor-v2
            ref="editor"
            :editable="false"
            height="auto"
            :smaller-headers="true"
            @ready="onEditorReady(goal)"
          />
        </q-card-section>
        <q-separator />
        <q-card-section class="goal-card-section goal-card-footer">
          <last-change
            :date="
              formatDateForTemplate({
                date: goal.createdAt,
                showTime: true,
              })
            "
            :editor-name="goal.editor.name"
          />
          <div v-if="hasEditPermission()" class="footer-checkboxes">
            <q-checkbox
              v-model="done"
              data-testid="done-goal"
              color="teal"
              class="checkbox"
              :label="t('userGoals.done')"
            />
            <q-checkbox
              v-model="active"
              data-testid="active-goal"
              color="teal"
              class="checkbox"
              :label="t('userGoals.active')"
            />
          </div>
        </q-card-section>
      </q-card>
    </c-page-section>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDateForTemplate } from '@/utils';
import CPageSection from '@/components/_common/PageSection.vue';
import LastChange from '@/components/_common/LastChange.vue';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import { hubClient } from '@tfpl/api-client';
import { parseISO } from 'date-fns';
import { shortDotFormat } from '@/utils/dateFormats';
import { permissions } from '@/store/modules/PositionModule';
import { useAuthorization } from '@/composables/auth/useAuthorization';

const { t } = useI18n();
const { hasLoggedUserPermission } = useAuthorization();

const props = defineProps<{
  goalIndex: number;
  goal: hubClient.CollaboratorGoalDto;
  allowRead: boolean;
  allowReadOwn: boolean;
  allowEdit: boolean;
}>();

const emit = defineEmits<{
  (e: 'click:done', { id, done }: { id: number; done: boolean }): void;
  (e: 'click:active', { id, active }: { id: number; active: boolean }): void;
  (e: 'click:edit', id: number): void;
  (e: 'click:delete', id: number): void;
}>();

const hasEditPermission = () =>
  hasLoggedUserPermission(permissions.HUB_USERS_GOALS.MANAGE_ALL) ||
  hasLoggedUserPermission(permissions.HUB_USERS_GOALS.MANAGE_TEAM_MEMBER);

const done = computed({
  get: () => props.goal.done,
  set: (done) => {
    emit('click:done', { id: props.goal.id, done });
  },
});

const active = computed({
  get: () => props.goal.active,
  set: (active) => {
    emit('click:active', { id: props.goal.id, active });
  },
});

const clickEdit = (id: number) => {
  emit('click:edit', id);
};

const clickDelete = (id: number) => {
  emit('click:delete', id);
};

const formatGoalNumber = (index: number) => {
  const num = index + 1;
  return t(`userGoals.goal`, { num });
};

const editor = ref();

const onEditorReady = (goal: hubClient.CollaboratorGoalDto) => {
  editor.value.setHTML(goal.message);
};
</script>
<style lang="scss" scoped>
@import 'styles/app.variables.scss';

.checkbox {
  border: none;
  height: auto;
  padding: 0;
}

.goal-done {
  background-color: $green-30;
}
.goal-after-deadline {
  background-color: $red-30;
}

.goal-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-checkboxes {
  display: flex;
  gap: 1rem;
}

.visible-user-name {
  display: inline-block;
  font-size: medium;
  position: relative;
  top: -2px;
}

.spinner {
  position: absolute;
  top: 35vh;
}
</style>
